import React, { useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import Footer from './components/Footer';
import Header from './components/Header';
import LoginRequiredModal from './components/LoginRequiredModal';
import TestCorsRequest from './components/TestCorsRequest';
import AdminLoginPage from './pages/AdminLoginPage';
import AppDetailPage from './pages/AppDetailPage';
import HomePage from './pages/HomePage';
import TestParamsPage from './pages/TestParamsPage';
import AdminAccountEditPage from './pages/admin/AdminAccountEditPage';
import AdminAccountsPage from './pages/admin/AdminAccountsPage';
import AdminAppCreatePage from './pages/admin/AdminAppCreatePage';
import AdminAppEditPage from './pages/admin/AdminAppEditPage';
import AdminAppVersionsPage from './pages/admin/AdminAppVersionsPage';
import AdminAppsPage from './pages/admin/AdminAppsPage';
import AdminForgotPasswordPage from './pages/admin/AdminForgotPasswordPage';
import AdminLayout from './pages/admin/AdminLayout';
import AdminLisenceDetailPage from './pages/admin/AdminLisenceDetailPage';
import AdminLisenceManagementPage from './pages/admin/AdminLisenceManagementPage';
import AdminResetPasswordPage from './pages/admin/AdminResetPasswordPage';
import AdminVerifyEmailPage from './pages/admin/AdminVerifyEmailPage';
import DownloadPage from './pages/admin/DownloadPage';
import { processUrlParams } from './utils/sessionUtils';
import { getSessionData } from './utils/storage';
import AdminRecorbManagement from './pages/admin/AdminRecorbManagement';

// Component to handle URL parameter processing and redirects
const UrlParamHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const handleProcessParams = async () => {
      // Check if we have token parameter in URL
      const hasToken = location.search.includes('token=');
      
      if (hasToken) {
        // Process/decrypt the token
        await processUrlParams();
      }

      if (!isMounted) return;

      // Mark that params have been checked and notify listeners FIRST
      // (before navigate to avoid re-render wiping out listeners)
      try {
        sessionStorage.setItem('paramsChecked', 'true');
        window.dispatchEvent(new Event('params-processed'));
      } catch {}

      // Always remove the token parameter from URL after processing
      // regardless of whether decryption succeeded or failed
      if (hasToken) {
        const params = new URLSearchParams(location.search);
        params.delete('token');
        const newSearch = params.toString();
        const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');

        // Use replace to avoid adding to history stack
        navigate(newPath, { replace: true });
      }
    };

    handleProcessParams();

    return () => {
      isMounted = false;
    };
  }, [location.pathname, location.search, navigate]);

  return null;
};

// Simple guard for admin dashboard based on localStorage token set by AdminLoginPage
const AdminProtected: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = (() => {
    try {
      return localStorage.getItem('admin-auth-token');
    } catch {
      return null;
    }
  })();
  const expiry = (() => {
    try {
      return localStorage.getItem('admin-auth-expiry');
    } catch {
      return null;
    }
  })();

  const isValid = token && expiry && Date.now() <= parseInt(expiry, 10);
  if (!isValid) {
    return <Navigate to='/admin/login' replace />;
  }
  return <>{children}</>;
};

const ScrollToTop: React.FC = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    if (isAdminRoute) return;

    window.scrollTo({ top: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [isAdminRoute, location.pathname]);

  return null;
};

// Router-aware content rendered inside BrowserRouter so we can use useLocation safely
const RouterContent: React.FC = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isAdminLogin = location.pathname === '/admin/login';

  // Lock scroll only when modal is visible
  useEffect(() => {
    document.body.style.overflow = !isAdminRoute && showLoginModal ? 'hidden' : '';
  }, [showLoginModal, isAdminRoute]);

  useEffect(() => {
    document.title = isAdminRoute ? 'Admin - Takagi Portal' : 'Home - Takagi App Download';
  }, [isAdminRoute]);

  // Session check: show login modal when session data missing
  useEffect(() => {
    if (isAdminRoute) return;

    const checkSessionData = () => {
      const { agentName, agencyEmail } = getSessionData();
      setShowLoginModal(!agentName || !agencyEmail);
    };

    const hasTokenInUrl = window.location.search.includes('token=');

    if (hasTokenInUrl) {
      // Token present: wait for decrypt to finish, don't check session yet
      const onParamsProcessed = () => checkSessionData();
      window.addEventListener('params-processed', onParamsProcessed, { once: true });

      const handleAppAuthRequired = () => setShowLoginModal(true);
      window.addEventListener('app-auth-required', handleAppAuthRequired);

      return () => {
        window.removeEventListener('params-processed', onParamsProcessed);
        window.removeEventListener('app-auth-required', handleAppAuthRequired);
      };
    } else {
      // No token: check session immediately
      checkSessionData();

      const handleAppAuthRequired = () => setShowLoginModal(true);
      window.addEventListener('app-auth-required', handleAppAuthRequired);

      return () => {
        window.removeEventListener('app-auth-required', handleAppAuthRequired);
      };
    }
  }, [isAdminRoute]);

  // Guard: require app access email/password (stored in localStorage) before allowing access to app pages.
  // This should happen after params are processed (token decoded -> sessionStorage set) to avoid redirect races.
  if (!isAdminRoute && showLoginModal) {
    return (
      <LoginRequiredModal
        isOpen={showLoginModal}
        force={true}
        loginUrl="https://www.takagi-global.com/en/members/"
        onClose={() => { /* forced modal, no-op */ }}
      />
    );
  }
  return (
    <div className='App' style={{ display: 'flex', minHeight: '100vh', flexDirection: 'column' }}>
      {!isAdminRoute && <UrlParamHandler />}
      <ScrollToTop />
      {!isAdminRoute && <Header />}
      <main style={{ display: 'flex', justifyContent: 'center', margin: 0, padding: 0 }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Dynamic app detail route */}
          <Route path='/app/:id' element={<AppDetailPage />} />
          {/* Legacy route kept temporarily; redirect to home */}
          <Route path='/app/recorb' element={<Navigate to='/' replace />} />
          <Route path='/test-cors' element={<TestCorsRequest />} />
          <Route path='/test-params' element={<TestParamsPage />} />
          <Route path='/admin/login' element={<AdminLoginPage />} />
          <Route path='/admin/forgot-password' element={<AdminForgotPasswordPage />} />
          <Route path='/admin/reset-password' element={<AdminResetPasswordPage />} />
          {/* Public route for admin email verification */}
          <Route path='/admin/verify-email' element={<AdminVerifyEmailPage />} />
          <Route
            path='/admin/*'
            element={
              <AdminProtected>
                <AdminLayout />
              </AdminProtected>
            }
          >
            <Route index element={<AdminAppsPage />} />
            <Route path="apps" element={<AdminAppsPage />} />
            <Route path="apps/new" element={<AdminAppCreatePage />} />
            <Route path="apps/:id/edit" element={<AdminAppEditPage />} />
            <Route path="apps/:id/versions" element={<AdminAppVersionsPage />} />
            <Route path="account-edit" element={<AdminAccountEditPage />} />
            <Route path="accounts" element={<AdminAccountsPage />} />
            <Route path="recorb-management" element={<AdminRecorbManagement />} />
            <Route path="license-management" element={<AdminLisenceManagementPage />} />
            <Route path="license-management/:id" element={<AdminLisenceDetailPage />} />
          </Route>
          <Route path='/plugin/:id' element={<DownloadPage />} />
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
      </main>
      {(!isAdminRoute || isAdminLogin) && <Footer />}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
}

export default App;
